
































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { listInfoConfigVersions, setClientInfoConfigTarget } from "@/data/repository/InfoConfigAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { infoConfigErrorKey } from "@/utils/InfoConfigAdministration";

/** The choice that removes the client's own target, so it follows the global one again. */
const FOLLOW_GLOBAL = "__global__";

/**
 * A client's own Beschreibung target: a version that is not retired and has a file, or "use the global
 * target". New projects of the client then start on it; existing ones keep theirs. IRES Cloud repeats
 * every check (`setInfoConfigTargetForAdministration`).
 *
 * `show()` resolves with true once saved.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class InfoConfigTargetDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  isLoading = false;
  busy = false;
  clientId = "";
  choice: string = FOLLOW_GLOBAL;
  initialChoice: string = FOLLOW_GLOBAL;
  items: Array<{ value: string; label: string }> = [];

  private resolve: ((saved: boolean) => void) | null = null;

  /**
   * @param clientId - the client.
   * @param effectiveTarget - the target the client detail shows: its own, else the global one.
   */
  show(clientId: string, effectiveTarget: string | null): Promise<boolean> {
    this.clientId = clientId;
    this.initialChoice = FOLLOW_GLOBAL;
    this.choice = FOLLOW_GLOBAL;
    this.items = [];
    this.baseDialog.show({
      title: this.$t("admin.infoConfig.target.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 520,
      persistent: true,
      closeOnSave: false,
    });
    this.loadItems(effectiveTarget);
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /**
   * The detail only knows the effective target, so a value other than the global one is the client's
   * own; one equal to it shows as "use the global target", which gives the same version.
   */
  async loadItems(effectiveTarget: string | null) {
    this.isLoading = true;
    try {
      const list = await listInfoConfigVersions();
      this.initialChoice = effectiveTarget !== null && effectiveTarget !== list.globalRoomTarget ? effectiveTarget : FOLLOW_GLOBAL;
      this.choice = this.initialChoice;
      this.items = [
        { value: FOLLOW_GLOBAL, label: this.$t("admin.infoConfig.target.followGlobal", [list.globalRoomTarget ?? "–"]).toString() },
        ...list.room.filter((row) => !row.retired && row.hasFile).map((row) => ({ value: row.version, label: row.version })),
      ];
    } catch (error) {
      SnackbarModule.showError({ key: infoConfigErrorKey(error) });
      this.close(false);
    } finally {
      this.isLoading = false;
    }
  }

  get hasChanged(): boolean {
    return this.choice !== this.initialChoice;
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(false);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(false);
    }
  }

  async onSave() {
    this.busy = true;
    try {
      await setClientInfoConfigTarget(this.clientId, this.choice === FOLLOW_GLOBAL ? null : this.choice);
      SnackbarModule.showSuccess({ key: "admin.infoConfig.target.saved" });
      this.close(true);
    } catch (error) {
      SnackbarModule.showError({ key: infoConfigErrorKey(error) });
    } finally {
      this.busy = false;
    }
  }

  close(saved: boolean) {
    this.baseDialog.isShowing = false;
    this.finish(saved);
  }

  private finish(saved: boolean) {
    this.resolve?.(saved);
    this.resolve = null;
  }
}
